import { safeGetByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";

type Operator = "in" | "equals" | "notEquals" | "notIn" | "hasValue";

interface LeafCondition {
  field: string;
  value?: any;
  operator: Operator;
}

interface GroupCondition {
  operator: "all" | "any";
  conditions: VisibilityCondition[];
}

export type VisibilityCondition = LeafCondition | GroupCondition;

function isVisibilityGroupCondition(
  visibility: VisibilityCondition
): visibility is GroupCondition {
  return "conditions" in visibility;
}

type OperatorStrategy = (currentValue: any, expectedValue?: any) => boolean;

/**
 * Strategy map for visibility operators
 * New operators can be added here without modifying existing code
 */
const operatorStrategies: Record<Operator, OperatorStrategy> = {
  in: (currentValue, expectedValue) =>
    Array.isArray(expectedValue) ? expectedValue.includes(currentValue) : false,

  equals: (currentValue, expectedValue) => currentValue === expectedValue,

  notEquals: (currentValue, expectedValue) => currentValue !== expectedValue,

  notIn: (currentValue, expectedValue) =>
    Array.isArray(expectedValue) ? !expectedValue.includes(currentValue) : true,

  hasValue: (currentValue) => {
    if (currentValue === null || currentValue === undefined) return false;
    if (typeof currentValue === "string") return currentValue.trim().length > 0;
    return true;
  },
};

/**
 * Determines if a field should be displayed based on visibility conditions
 * @param task - The task object containing field values
 * @param visibility - The visibility condition to evaluate
 * @returns true if the field should be shown, false otherwise
 */
export function shouldShowField(
  task: Record<string, any>,
  visibility: VisibilityCondition
): boolean {
  if (isVisibilityGroupCondition(visibility) && visibility.operator === "all") {
    return visibility.conditions.every((condition) =>
      shouldShowField(task, condition)
    );
  }

  if (isVisibilityGroupCondition(visibility) && visibility.operator === "any") {
    return visibility.conditions.some((condition) =>
      shouldShowField(task, condition)
    );
  }

  const leafCondition = visibility as LeafCondition;

  const currentValue = safeGetByPath(task, leafCondition.field);
  const strategy = operatorStrategies[leafCondition.operator];

  if (!strategy) {
    console.warn(
      `Unknown operator: ${visibility.operator}, defaulting to true`
    );
    return true;
  }

  const normalizedCurrentValue =
    leafCondition.operator === "hasValue"
      ? currentValue
      : typeof currentValue === "object" &&
          currentValue !== null &&
          "value" in currentValue
        ? currentValue.value
        : currentValue;

  return strategy(normalizedCurrentValue, leafCondition.value);
}
